import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import './BirthdayPicker.css';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const DAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function buildMonth(year, month) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();

  const cells = [];
  for (let i = firstDay - 1; i >= 0; i--) {
    cells.push({ day: daysInPrevMonth - i, current: false });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(year, month, d);
    date.setHours(0, 0, 0, 0);
    cells.push({ day: d, current: true, date });
  }
  const remaining = 7 - (cells.length % 7);
  if (remaining < 7) {
    for (let d = 1; d <= remaining; d++) {
      cells.push({ day: d, current: false });
    }
  }

  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) {
    weeks.push(cells.slice(i, i + 7));
  }
  return { year, month, label: `${MONTH_NAMES[month]} ${year}`, weeks };
}

function buildMonths(startYear, startMonth, count) {
  const months = [];
  let y = startYear, m = startMonth;
  for (let i = 0; i < count; i++) {
    months.push(buildMonth(y, m));
    m++;
    if (m > 11) { m = 0; y++; }
  }
  return months;
}

class BirthdayPicker extends Component {
  constructor(props) {
    super(props);
    const selected = props.selected ? new Date(props.selected) : new Date();
    let startMonth = selected.getMonth() - 3;
    let startYear = selected.getFullYear();
    if (startMonth < 0) { startMonth += 12; startYear--; }

    this.state = {
      viewYear: selected.getFullYear(),
      viewMonth: selected.getMonth(),
      showYearGrid: false,
      showMobileYearGrid: false,
      isMobile: typeof window !== 'undefined' && window.innerWidth < 768,
      months: buildMonths(startYear, startMonth, 12),
      startYear,
      startMonth,
    };
    this.scrollRef = React.createRef();
    this.overlayRef = React.createRef();
    this.isExtending = false;
  }

  componentDidMount() {
    this._resizeHandler = () => {
      this.setState({ isMobile: window.innerWidth < 768 });
    };
    window.addEventListener('resize', this._resizeHandler);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this._resizeHandler);
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.open && this.props.open) {
      const selected = this.props.selected ? new Date(this.props.selected) : new Date();

      // Desktop: reset single-month view
      let startMonth = selected.getMonth() - 3;
      let startYear = selected.getFullYear();
      if (startMonth < 0) { startMonth += 12; startYear--; }
      const months = buildMonths(startYear, startMonth, 12);

      this.setState({
        viewYear: selected.getFullYear(),
        viewMonth: selected.getMonth(),
        showYearGrid: false,
        showMobileYearGrid: false,
        months,
        startYear,
        startMonth,
      }, () => {
        // Scroll to selected month on mobile
        if (this.state.isMobile && this.scrollRef.current) {
          const idx = this.state.months.findIndex(
            m => m.year === selected.getFullYear() && m.month === selected.getMonth()
          );
          if (idx >= 0) {
            const monthEls = this.scrollRef.current.querySelectorAll('.bp-month-block');
            if (monthEls[idx]) {
              monthEls[idx].scrollIntoView({ block: 'start' });
            }
          }
        }
      });
    }
  }

  // Mobile infinite scroll
  handleScroll = () => {
    if (!this.scrollRef.current || this.isExtending) return;
    const el = this.scrollRef.current;

    if (el.scrollTop < 200) {
      this.isExtending = true;
      const prevHeight = el.scrollHeight;
      let { startYear, startMonth } = this.state;
      startMonth -= 6;
      if (startMonth < 0) { startMonth += 12; startYear--; }
      const newMonths = buildMonths(startYear, startMonth, 6);
      this.setState(prev => ({
        months: [...newMonths, ...prev.months],
        startYear,
        startMonth,
      }), () => {
        const newHeight = el.scrollHeight;
        el.scrollTop += newHeight - prevHeight;
        this.isExtending = false;
      });
    }

    if (el.scrollTop + el.clientHeight > el.scrollHeight - 200) {
      this.isExtending = true;
      const last = this.state.months[this.state.months.length - 1];
      let nextMonth = last.month + 1;
      let nextYear = last.year;
      if (nextMonth > 11) { nextMonth = 0; nextYear++; }
      const newMonths = buildMonths(nextYear, nextMonth, 6);
      this.setState(prev => ({
        months: [...prev.months, ...newMonths],
      }), () => {
        this.isExtending = false;
      });
    }
  }

  // Desktop single-month nav
  prevMonth = () => {
    this.setState(prev => {
      let m = prev.viewMonth - 1;
      let y = prev.viewYear;
      if (m < 0) { m = 11; y--; }
      return { viewMonth: m, viewYear: y };
    });
  }

  nextMonth = () => {
    this.setState(prev => {
      let m = prev.viewMonth + 1;
      let y = prev.viewYear;
      if (m > 11) { m = 0; y++; }
      return { viewMonth: m, viewYear: y };
    });
  }

  selectYear = (year) => {
    this.setState({ viewYear: year, showYearGrid: false });
  }

  selectMobileYear = (year) => {
    // Jump the scroll view to January of selected year
    let startMonth = 0;
    let startYear = year;
    const months = buildMonths(startYear, startMonth, 12);
    this.setState({
      months,
      startYear,
      startMonth,
      showMobileYearGrid: false,
    }, () => {
      if (this.scrollRef.current) {
        this.scrollRef.current.scrollTop = 0;
      }
    });
  }

  handleDayClick = (date) => {
    if (this.props.onChange) {
      this.props.onChange(date);
    }
  }

  handleOverlayClick = (e) => {
    if (e.target === this.overlayRef.current && this.props.onClose) {
      this.props.onClose();
    }
  }

  // Desktop year grid
  renderYearGrid() {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = currentYear; y >= currentYear - 100; y--) {
      years.push(y);
    }

    return (
      <div className="bp-year-grid">
        {years.map(y => (
          <button
            key={y}
            type="button"
            className={'bp-year-cell' + (y === this.state.viewYear ? ' bp-year-cell--selected' : '')}
            onClick={() => this.selectYear(y)}
          >
            {y}
          </button>
        ))}
      </div>
    );
  }

  // Desktop single-month calendar
  renderCalendar() {
    const { viewYear, viewMonth, showYearGrid } = this.state;
    const monthData = buildMonth(viewYear, viewMonth);
    const selected = this.props.selected ? new Date(this.props.selected) : null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return (
      <div className="bp-calendar">
        <div className="bp-nav">
          <button type="button" className="bp-nav-btn" onClick={this.prevMonth}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <button type="button" className="bp-nav-title" onClick={() => this.setState({ showYearGrid: !showYearGrid })}>
            {MONTH_NAMES[viewMonth]} {viewYear}
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginLeft: 4, transform: showYearGrid ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>
          <button type="button" className="bp-nav-btn" onClick={this.nextMonth}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
        </div>

        {showYearGrid ? this.renderYearGrid() : (
          <>
            <div className="bp-day-labels">
              {DAY_LABELS.map(d => <span key={d} className="bp-day-label">{d}</span>)}
            </div>
            <div className="bp-grid">
              {monthData.weeks.map((week, wi) => (
                <div key={wi} className="bp-week">
                  {week.map((cell, ci) => {
                    const isToday = cell.date && cell.date.getTime() === today.getTime();
                    const isSelected = cell.date && selected &&
                      cell.date.getFullYear() === selected.getFullYear() &&
                      cell.date.getMonth() === selected.getMonth() &&
                      cell.date.getDate() === selected.getDate();
                    return (
                      <button
                        key={ci}
                        type="button"
                        className={
                          'bp-cell' +
                          (!cell.current ? ' bp-cell--other' : '') +
                          (isToday ? ' bp-cell--today' : '') +
                          (isSelected ? ' bp-cell--selected' : '')
                        }
                        disabled={!cell.current}
                        onClick={() => cell.date && this.handleDayClick(cell.date)}
                      >
                        {cell.day}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  // Mobile: month block for scrollable calendar
  renderMonthBlock(monthData, key) {
    const selected = this.props.selected ? new Date(this.props.selected) : null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return (
      <div className="bp-month-block" key={key}>
        <div className="bp-month-title">{monthData.label}</div>
        <div className="bp-day-labels">
          {DAY_LABELS.map(d => <span key={d} className="bp-day-label">{d}</span>)}
        </div>
        <div className="bp-grid">
          {monthData.weeks.map((week, wi) => (
            <div key={wi} className="bp-week">
              {week.map((cell, ci) => {
                const isToday = cell.date && cell.date.getTime() === today.getTime();
                const isSelected = cell.date && selected &&
                  cell.date.getFullYear() === selected.getFullYear() &&
                  cell.date.getMonth() === selected.getMonth() &&
                  cell.date.getDate() === selected.getDate();
                return (
                  <button
                    key={ci}
                    type="button"
                    className={
                      'bp-cell' +
                      (!cell.current ? ' bp-cell--other' : '') +
                      (isToday ? ' bp-cell--today' : '') +
                      (isSelected ? ' bp-cell--selected' : '')
                    }
                    disabled={!cell.current}
                    onClick={() => cell.date && this.handleDayClick(cell.date)}
                  >
                    {cell.day}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Mobile year grid (full-screen overlay within the sheet)
  renderMobileYearGrid() {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = currentYear; y >= currentYear - 100; y--) {
      years.push(y);
    }

    // Determine which year is "active" based on visible months
    const activeYear = this.state.months.length > 3
      ? this.state.months[3].year
      : currentYear;

    return (
      <div className="bp-mobile-year-grid">
        {years.map(y => (
          <button
            key={y}
            type="button"
            className={'bp-mobile-year-cell' + (y === activeYear ? ' bp-mobile-year-cell--selected' : '')}
            onClick={() => this.selectMobileYear(y)}
          >
            {y}
          </button>
        ))}
      </div>
    );
  }

  render() {
    if (!this.props.open) return null;

    if (this.state.isMobile) {
      // Determine displayed year from visible months
      const displayedYear = this.state.months.length > 3
        ? this.state.months[3].year
        : new Date().getFullYear();

      return ReactDOM.createPortal(
        <div className="bp-mobile-overlay" ref={this.overlayRef} onClick={this.handleOverlayClick}>
          <div className="bp-mobile-sheet">
            <div className="bp-sheet-header">
              <span className="bp-sheet-title">Birthday</span>
              <button type="button" className="bp-sheet-close" onClick={this.props.onClose}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
            <div className="bp-sheet-divider" />
            <button
              type="button"
              className="bp-year-selector"
              onClick={() => this.setState(prev => ({ showMobileYearGrid: !prev.showMobileYearGrid }))}
            >
              <span className="bp-year-selector-text">{displayedYear}</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ transform: this.state.showMobileYearGrid ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>
            {this.state.showMobileYearGrid ? (
              this.renderMobileYearGrid()
            ) : (
              <div
                className="bp-scroll-container"
                ref={this.scrollRef}
                onScroll={this.handleScroll}
              >
                {this.state.months.map((m) => this.renderMonthBlock(m, `${m.year}-${m.month}`))}
              </div>
            )}
            <div className="bp-sheet-footer">
              <button type="button" className="bp-sheet-cancel" onClick={this.props.onClose}>Cancel</button>
              <button type="button" className="bp-sheet-confirm" onClick={this.props.onClose}>Confirm</button>
            </div>
          </div>
        </div>,
        document.body
      );
    }

    // Desktop: dropdown with single month + year grid toggle
    return ReactDOM.createPortal(
      <>
        <div className="bp-backdrop" onClick={this.props.onClose} />
        <div className="bp-dropdown" style={this.getPosition()}>
          {this.renderCalendar()}
        </div>
      </>,
      document.body
    );
  }

  getPosition() {
    if (!this.props.anchorEl) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    const rect = this.props.anchorEl.getBoundingClientRect();
    const dropdownHeight = 340;
    const spaceBelow = window.innerHeight - rect.bottom;
    const showAbove = spaceBelow < dropdownHeight && rect.top > dropdownHeight;

    return {
      position: 'fixed',
      left: rect.left,
      ...(showAbove
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }
      ),
    };
  }
}

export default BirthdayPicker;
